import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Container,
    Stack,
    TextField,
    Switch,
    FormControlLabel,
    CircularProgress,
    Alert,
    Chip, // Keep if you use it elsewhere, but not for member display now
    IconButton, // Keep if you use it elsewhere
    Tooltip,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useNotification } from '../notification/NotificationProvider';
import { createGroup } from '../auth/MisBolosApi'; // Assuming this is still used
// Import the new component
import GroupMemberManager from './GroupMemberManager.jsx'; // Adjust path as needed
// Import Leaflet components and fix (keep these as they are for the map)
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});



// --- Helper function for image compression (from your YouKnowFormScreen) ---
const compressImage = (file, maxSizeKb = 20) => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                const maxDim = 1200;
                if (width > maxDim || height > maxDim) {
                    if (width > height) {
                        height *= maxDim / width;
                        width = maxDim;
                    } else {
                        width *= maxDim / height;
                        height = maxDim;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                let quality = 0.9;
                let dataUrl = canvas.toDataURL('image/jpeg', quality);
                let fileSize = dataUrl.length * (3 / 4) - 2;

                while (fileSize > maxSizeKb * 1024 && quality > 0.1) {
                    quality -= 0.1;
                    dataUrl = canvas.toDataURL('image/jpeg', quality);
                    fileSize = dataUrl.length * (3 / 4) - 2;
                }

                resolve(dataUrl);
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(file);
    });
};

// --- MapClickEventHandler Component ---
function MapClickEventHandler({ setMapCenter, setLocationData }) {
    useMapEvents({
        click: (e) => {
            const { lat, lng } = e.latlng;
            setMapCenter([lat, lng]);
            setLocationData({ latitude: lat, longitude: lng });
            console.log(`New map center and location set to: [${lat}, ${lng}]`);
        },
    });
    return null;
}

const CreateGroupForm = () => {
    const location = useLocation();
    const { editMode, editProps } = location.state || {};
    console.log('editmode', !!editMode, 'editProps', !!editProps);

    const { currentUser, isLoading: authLoading, dbUser } = useAuth(); // Renamed isLoading to authLoading to avoid conflict
    const { triggerNotification } = useNotification();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        name: editMode && editProps?.name ? editProps.name : '',
        description: editMode && editProps?.description ? editProps.description : '',
        services: editMode && editProps?.services ? editProps.services : '',
        birthdate: editMode && editProps?.birthdate ? editProps.birthdate : '',
        active: editMode && typeof editProps?.active === 'boolean' ? editProps.active : true,
        links: editMode && editProps?.links ? editProps.links : {},
        location: editMode && editProps?.location ? editProps.location : null,
    });

    console.log('Calculated formData from parent', "<formData>")

    const [mapCenter, setMapCenter] = useState(
        editMode && editProps?.location
            ? [editProps.location.latitude, editProps.location.longitude]
            : [43.3623, -8.3946] // Default to A Coruña
    );

    const [images, setImages] = useState(editMode && editProps?.images ? editProps.images : []);
    const fileInputRef = useRef(null);

    // This state will now be controlled by GroupMemberManager
    const [members, setMembers] = useState(editMode && editProps?.members ? editProps.members : []);

    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        if (!authLoading && dbUser) { // Wait for dbUser to be loaded
            try {
                // Ensure dbUser._id is available for comparison
                setIsAdmin(dbUser._id == editProps?.owner);
                console.log('set isAdmin?', dbUser._id == editProps?.owner, editProps.owner, dbUser._id);
            } catch (error) {
                console.log('Error catched when attempting to calculate if user is admin of this group', error);
                setIsAdmin(false); // Default to false if there's an error or no editProps
            }

            // Ensure the current user is always a member if no members are provided initially
            // And if not in edit mode (creating a new group)
            if (!editMode && currentUser && currentUser.email && members.length === 0) {
                // Assuming members are stored by email for consistency with dbUser.getPublicUsers
                // CAUTION
                setMembers([{ email: currentUser.email }]);
            } else if (editMode && editProps && editProps.members && members.length === 0) {
                // If in edit mode and members are provided, ensure they are loaded
                setMembers(editProps.members);
            }
        }
    }, [currentUser, members.length, authLoading, dbUser, editMode, editProps]);


    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleLinksChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            links: {
                ...prevData.links,
                [name]: value,
            },
        }));
    };

    const handleSetLocationData = (newLocation) => {
        setFormData((prevData) => ({
            ...prevData,
            location: newLocation,
        }));
    };

    const handleImageChange = async (event) => {
        const files = Array.from(event.target.files);
        const newImages = [...images];

        for (let i = 0; i < files.length && newImages.length < 3; i++) {
            const file = files[i];
            if (file.type.startsWith('image/')) {
                try {
                    const compressedDataUrl = await compressImage(file, 350);
                    newImages.push(compressedDataUrl);
                } catch (error) {
                    console.error("Error compressing image:", error);
                    setErrorMessage('Failed to process image. Please try a different one.');
                }
            } else {
                setErrorMessage('Only image files are allowed.');
            }
        }
        setImages(newImages);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveImage = (indexToRemove) => {
        setImages((prevImages) => prevImages.filter((_, index) => index !== indexToRemove));
    };

    // New callback function to receive updated members from GroupMemberManager
    const handleMembersUpdate = (updatedMembers) => {
        setMembers(updatedMembers);
        // Optionally trigger a notification or message here based on the update
        console.log("Members updated by GroupMemberManager:", updatedMembers);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {

            //CAUTION: members is like [{email: '...'}] -> needs to be flatMapped

            const membersToSend = members.flatMap(m => m.email)

            if (!formData.name || !formData.birthdate) {
                throw new Error('Name and Birthdate are required fields.');
            }
            if (membersToSend.length === 0) {
                throw new Error('At least one member is required for the group.');
            }


            let groupData = {
                name: formData.name,
                description: formData.description,
                services: formData.services,
                birthdate: formData.birthdate,
                active: formData.active,
                links: formData.links,
                members: membersToSend,
                images: images,
                location: formData.location,
            };

            console.log("Submitting Group Data");
            let realresponse;
            if (editMode) {
                if (!isAdmin) {
                    throw new Error('You do not have permission to edit this group.');
                }
                console.log('Using update group');
                realresponse = await dbUser.updateGroup(editProps._id, groupData);
                triggerNotification({
                    message: `Has actualizado la información de ${groupData.name}`,
                    sound: true,
                    show: true,
                    type: 'tiny-top',
                    cat: 'received-request-from-group',
                    forceId: 'new-group-handle-submit-edit-success'
                });
                await dbUser.update();
                if (realresponse) {
                    navigate('/misbolos/admin')
                    // window.location.reload(); // TODO: MAke a refresh function to avoid reload
                }
            } else {
                console.log('Using create group');
                realresponse = await dbUser.createGroup(groupData);


                if (realresponse) {
                    triggerNotification({
                        message: `Grupo ${groupData.name} creado correctamente`,
                        sound: true,
                        show: true,
                        type: 'tiny-top',
                        cat: 'received-request-from-group',
                        forceId: 'new-group-handle-submit-create-success'
                    });
                    await dbUser.update();
                    navigate('/misbolos/admin')
                    // window.location.reload(); // TODO: MAke a refresh function to avoid reload
                } else {
                    let reason = 'No se pudo crear el grupo ...return more info from api...'
                    triggerNotification({
                        message: `${reason}`,
                        sound: true,
                        show: true,
                        type: 'tiny-top',
                        cat: 'received-request-from-group',
                        forceId: 'new-group-handle-submit-create-error',
                        isError: true
                    });
                    // I repeat this line because i want to let it load until navigate in the other snnippet
                    await dbUser.update();
                }

            }

            setSuccessMessage(realresponse.message);
            // Optionally navigate or clear form
        } catch (error) {
            console.error('Error creating/updating group:', error);
            triggerNotification({
                message: `No se ha podido guardar ${error.message}`,
                sound: true,
                show: true,
                type: 'tiny-top',
                cat: 'received-request-from-group',
                forceId: 'new-group-handle-submit-error',
                isError: true
            });
            setSuccessMessage('');
            setErrorMessage(error.message || 'An unexpected error occurred.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            id="newgroup-component"
            sx={{
                minHeight: '100vh',
                ////background: 'linear-gradient(to right, #f8fafc, #e0f2fe)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 2,
            }}
        >
            <Container maxWidth="sm">
                <Stack spacing={4} alignItems="center" textAlign="center">
                    <Typography variant="h3" fontWeight="bold">
                        {editMode ? 'Editar Grupo' : 'Nuevo Grupo'}
                    </Typography>

                    <Typography variant="h6" color="text.secondary">
                        {editMode ? 'Añade miembros o modifica las propiedades' : 'Completa los campos para crear un nuevo grupo'}
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', mt: 3, p: 3, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: 'white', boxShadow: 3 }}>
                        <Stack spacing={3}>
                            <TextField
                                label="Nombre del Grupo (único para cada banda)"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                fullWidth
                                required
                                variant="outlined"
                                disabled={editMode && !isAdmin} // Disable if in edit mode and not admin
                            />
                            <TextField
                                label="Descripción (información relevante de la banda)"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                fullWidth
                                multiline
                                rows={3}
                                variant="outlined"
                                disabled={editMode && !isAdmin}
                            />
                            <TextField
                                label="Servicios (forma de contacto, condiciones...)"
                                name="services"
                                value={formData.services}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                                disabled={editMode && !isAdmin}
                            />
                            <TextField
                                label="Aniversario (Fecha de creación)"
                                name="birthdate"
                                type="date"
                                value={formData.birthdate ? new Date(formData.birthdate).toISOString().split('T')[0] : ''}
                                onChange={handleChange}
                                fullWidth
                                required
                                variant="outlined"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                disabled={editMode && !isAdmin}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.active}
                                        onChange={handleChange}
                                        name="active"
                                        color="primary"
                                        disabled={editMode && !isAdmin}
                                    />
                                }
                                label="Actualmente Activo"
                            />

                            {/* Section for Location Map */}
                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Localización del Grupo</Typography>
                            <Box sx={{ height: 300, width: '100%', border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                                <MapContainer
                                    center={mapCenter}
                                    zoom={13}
                                    scrollWheelZoom={false}
                                    style={{ height: '100%', width: '100%' }}
                                >
                                    <TileLayer
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                    {formData.location && (
                                        <Marker position={[formData.location.latitude, formData.location.longitude]} />
                                    )}
                                    {/* Map interaction is disabled if not admin in edit mode */}
                                    {!(editMode && !isAdmin) && <MapClickEventHandler setMapCenter={setMapCenter} setLocationData={handleSetLocationData} />}
                                </MapContainer>
                            </Box>
                            {formData.location && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Localización Actual: Latitud {formData.location.latitude.toFixed(4)}, Longitud {formData.location.longitude.toFixed(4)}
                                </Typography>
                            )}

                            {/* Section for Links */}
                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>WebSite</Typography>
                            <TextField
                                label="Web (en blanco: web gratuita automática"
                                name="website"
                                value={formData.links.website || ''}
                                onChange={handleLinksChange}
                                fullWidth
                                variant="outlined"
                                disabled={editMode && !isAdmin}
                            />
                            {/* <TextField
                                label="Facebook URL"
                                name="facebook"
                                value={formData.links.facebook || ''}
                                onChange={handleLinksChange}
                                fullWidth
                                variant="outlined"
                                disabled={editMode && !isAdmin}
                            /> */}

                            {/* Render the new GroupMemberManager component only if groupId */}
                            {(editMode && editProps && editProps._id) ? (
                                <GroupMemberManager
                                    initialMembers={members}
                                    pendingMembers={editProps?.pendingMembers || []}
                                    onMembersChange={handleMembersUpdate}
                                    groupId={editProps._id}
                                    isAdmin={dbUser._id === editProps.owner}
                                />
                            ) : (
                                <Typography variant="body2" color="textSecondary">
                                    {/* Los miembros se pueden añadir una vez creado el grupo en MisBandas */}
                                </Typography>
                            )}


                            {/* Section for Images */}
                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Foto de Perfil</Typography>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleImageChange}
                                disabled={editMode && !isAdmin}
                            />
                            <Button
                                variant="outlined"
                                onClick={() => fileInputRef.current.click()}
                                disabled={images.length >= 3 || (editMode && !isAdmin)}
                                sx={{ mb: 2 }}
                            >
                                Imagen
                            </Button>
                            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
                                {images.map((imageSrc, index) => (
                                    <Box key={index} sx={{ position: 'relative', width: 100, height: 100, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
                                        <img
                                            src={imageSrc}
                                            alt={`Preview ${index + 1}`}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRemoveImage(index)}
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                right: 0,
                                                backgroundColor: 'rgba(255,255,255,0.7)',
                                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                                                p: '2px',
                                            }}
                                            disabled={editMode && !isAdmin}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Stack>

                            {/* Feedback Messages */}
                            {successMessage && (
                                <Alert severity="success">{successMessage}</Alert>
                            )}
                            {errorMessage && (
                                <Alert severity="error">{errorMessage}</Alert>
                            )}

                            <Tooltip
                                title={editMode && !isAdmin ? 'You must be an admin to perform this action.' : ''}
                                disableHoverListener={!(editMode && !isAdmin)}
                            >
                                <span>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        fullWidth
                                        disabled={
                                            loading ||
                                            members.length === 0 ||
                                            (editMode && !isAdmin)
                                        }
                                        sx={{
                                            borderRadius: 2,
                                            paddingY: 1.5,
                                        }}
                                    >
                                        {loading ? <CircularProgress size={24} color="inherit" /> : (editMode ? 'Guardar Cambios' : 'Crear Grupo')}
                                    </Button>
                                </span>
                            </Tooltip>
                        </Stack>
                    </Box>
                </Stack>
            </Container>
        </Box>
    );
};

export default CreateGroupForm;